import { useState, useRef, useEffect, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import imageCompression from 'browser-image-compression';

interface ImageCropModalProps {
  originalFile: File;
  onCropComplete: (croppedWebPFile: File) => void;
  onCancel: () => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  originalFile,
  onCropComplete,
  onCancel
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [compressionQuality, setCompressionQuality] = useState<number>(0.9);
  const [zoom, setZoom] = useState<number>(1);
  const [estimatedSize, setEstimatedSize] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load image on mount
  useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(originalFile);
  }, [originalFile]);

  // Initialize crop as a perfect square when image loads
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Calculate square crop size (90% of the smaller dimension)
    const cropSize = Math.min(width, height) * 0.9;
    
    // Center the crop
    const x = (width - cropSize) / 2;
    const y = (height - cropSize) / 2;
    
    const squareCrop: Crop = {
      unit: 'px',
      width: cropSize,
      height: cropSize,
      x: x,
      y: y
    };
    
    setCrop(squareCrop);
    console.log('🔲 Initialized square crop:', cropSize, 'x', cropSize);
  }, []);

  // Generate preview canvas when crop changes
  const generatePreviewCanvas = useCallback(
    (image: HTMLImageElement, crop: PixelCrop) => {
      if (!canvasRef.current) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Account for zoom when calculating crop coordinates
      const scaleX = (image.naturalWidth / image.width) / zoom;
      const scaleY = (image.naturalHeight / image.height) / zoom;

      const targetSize = 1024;
      canvas.width = targetSize;
      canvas.height = targetSize;

      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        targetSize,
        targetSize
      );

      // Estimate WebP size
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const sizeKB = Math.round(blob.size / 1024);
            setEstimatedSize(`${sizeKB}KB`);
          }
        },
        'image/webp',
        compressionQuality
      );
    },
    [compressionQuality, zoom]
  );

  // Update preview when crop, quality, or zoom changes
  useEffect(() => {
    if (imgRef.current && completedCrop) {
      generatePreviewCanvas(imgRef.current, completedCrop);
    }
  }, [completedCrop, compressionQuality, zoom, generatePreviewCanvas]);

  // Apply crop and convert to WebP
  const handleApplyCrop = async () => {
    if (!imgRef.current || !completedCrop) {
      alert('Please select a crop area first.');
      return;
    }

    setProcessing(true);

    try {
      // 1. Create canvas and draw cropped image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Account for zoom when calculating crop coordinates
      const scaleX = (imgRef.current.naturalWidth / imgRef.current.width) / zoom;
      const scaleY = (imgRef.current.naturalHeight / imgRef.current.height) / zoom;

      // Set canvas to 1024x1024 (optimal for certificates)
      const targetSize = 1024;
      canvas.width = targetSize;
      canvas.height = targetSize;

      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        imgRef.current,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        targetSize,
        targetSize
      );

      // 2. Convert canvas to WebP blob
      const webpBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create WebP blob'));
            }
          },
          'image/webp',
          compressionQuality
        );
      });

      console.log(`🎨 WebP created: ${webpBlob.size} bytes (quality: ${Math.round(compressionQuality * 100)}%)`);

      // 3. Check size and compress if needed
      let finalBlob = webpBlob;
      if (webpBlob.size > 512000) {
        // Still too large - apply additional compression
        console.log('⚠️ Image >500KB, applying additional compression...');
        
        const tempFile = new File([webpBlob], 'temp.webp', { type: 'image/webp' });
        finalBlob = await imageCompression(tempFile, {
          maxSizeMB: 0.48, // Target 480KB to be safe
          useWebWorker: true,
          fileType: 'image/webp',
          initialQuality: compressionQuality
        });

        console.log(`🗜️ Compressed to: ${finalBlob.size} bytes`);
      }

      // 4. Final size check
      if (finalBlob.size > 512000) {
        const overage = Math.round((finalBlob.size - 512000) / 1024);
        alert(
          `❌ Image is still ${overage}KB too large (${Math.round(finalBlob.size / 1024)}KB).\n\n` +
          `Try:\n` +
          `• Crop closer to the subject\n` +
          `• Lower quality to 70-80%\n` +
          `• Current quality: ${Math.round(compressionQuality * 100)}%`
        );
        setProcessing(false);
        return;
      }

      // 5. Create File object
      const timestamp = Date.now();
      const webpFile = new File(
        [finalBlob],
        `cropped_${timestamp}.webp`,
        { type: 'image/webp' }
      );

      console.log(`✅ Final WebP: ${webpFile.size} bytes (${Math.round(webpFile.size / 1024)}KB)`);

      onCropComplete(webpFile);

    } catch (error) {
      console.error('❌ Crop error:', error);
      alert(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="crop-modal-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="crop-modal">
        <h3>📐 Crop Your Image (1:1 Square)</h3>

        <div className="crop-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={1}
            minWidth={50}
            minHeight={50}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              onLoad={onImageLoad}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '500px',
                transform: `scale(${zoom})`,
                transformOrigin: 'center center',
                transition: 'transform 0.1s ease-out'
              }}
            />
          </ReactCrop>
        </div>

        {/* Preview Canvas (hidden) */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Zoom Control */}
        <div className="zoom-control">
          <label>
            <span>🔍 Zoom: {zoom.toFixed(1)}x</span>
          </label>
          <input
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <p className="zoom-hint">
            💡 Zoom in to fit your subject perfectly in the square
          </p>
        </div>

        {/* Quality Control */}
        <div className="quality-control">
          <label>
            <span>🎨 Quality: {Math.round(compressionQuality * 100)}%</span>
            {estimatedSize && <span className="estimated-size"> (~{estimatedSize})</span>}
          </label>
          <input
            type="range"
            min="0.6"
            max="1.0"
            step="0.05"
            value={compressionQuality}
            onChange={(e) => setCompressionQuality(parseFloat(e.target.value))}
          />
          <p className="quality-hint">
            💡 Lower quality = smaller file. Recommended: 85-90%
          </p>
        </div>

        {/* Buttons */}
        <div className="crop-actions">
          <button onClick={onCancel} disabled={processing} className="btn-cancel">
            ❌ Cancel
          </button>
          <button onClick={handleApplyCrop} disabled={processing} className="btn-apply">
            {processing ? '⏳ Processing...' : '✅ Apply Crop'}
          </button>
        </div>

        <p className="crop-hint">
          💡 Drag the box to select area. Resize corners for perfect framing. Target: {'<'}500KB
        </p>
      </div>
    </div>
  );
};

